<template>
  <div class="edit-avatar bg-background" :class="{ invisible: isCameraActive }">
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      <div>
        プロフィール写真
      </div>
    </Header>

    <RejectionReason
      v-if="isRejected"
      :details="rejectionReasonDetails"
      :comment="rejectionReasonComment"
    />

    <div class="py-4 mx-3">
      <SetAvatar
        v-model="inputValues"
        v-model:isCameraActive="isCameraActive"
        :disabled="isUnderExamination"
        :disabledText="disabledText"
      />
      <AppButton
        block
        class="mt-4"
        @click="onClick"
        :disabled="isUnderExamination"
      >
        {{ buttonLabel }}
      </AppButton>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import Header from "@/components/organisms/common/Header.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { authStore } from "@/store/auth";
import VerifiedStatus from "@/models/verifiedStatus";
import { accountService } from "@/app/services/AccountService";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import SetAvatar from "@/components/organisms/register/SetAvatar.vue";
import { AvatarInputValues } from "@/types/user/RequestVerificationQueueBody";
import RejectionReason from "@/components/atoms/other/RejectionReason.vue";
import RejectionReasonTitle from "@/models/rejectionReasonTitle";

@Component({
  components: {
    Header,
    AppButton,
    FeatherIcon,
    SetAvatar,
    RejectionReason
  }
})
export default class MypageEditAvatar extends Vue {
  inputValues: AvatarInputValues = {
    avatar: null
  };

  isCameraActive = false;
  rejectionReasonComment: string | null = null;
  requestQueueUpdatedAt: string | null = null;

  get user() {
    return authStore.user!;
  }

  get isRejected() {
    return (
      this.user.verifiedStatus === VerifiedStatus.REJECTED &&
      this.user.rejectionReason.title === RejectionReasonTitle.AVATAR
    );
  }

  get rejectionReasonDetails() {
    return this.user.rejectionReason.details;
  }

  get isUnderExamination() {
    if (!this.hasRegisteredAvatar) return false;
    if (!this.hasRegisteredProfile) return false;
    if (this.user.verifiedStatus === VerifiedStatus.UNVERIFIED) {
      return true;
    }
    return false;
  }

  get hasRegisteredAvatar() {
    return authStore.hasRegisteredAvatar;
  }

  get hasRegisteredProfile() {
    return this.user.hasRegisteredProfile;
  }

  get disabledText() {
    return "審査中は変更できません。";
  }

  get buttonLabel() {
    if (this.isUnderExamination) return "審査中は変更できません";
    return "保存する";
  }

  async created() {
    try {
      this.$loading.show();
      const latestRequestVerificationQueue = await accountService.fetchLatestRequestVerificationQueue();
      this.inputValues.avatar =
        latestRequestVerificationQueue?.avatar ?? this.user.avatar;
      this.rejectionReasonComment =
        latestRequestVerificationQueue?.comment || null;
      this.requestQueueUpdatedAt = latestRequestVerificationQueue?.updatedAt;
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async onClick() {
    if (!this.inputValues.avatar) {
      this.$bvModal.msgBoxOk(
        "プロフィール写真を撮影、またはファイル選択してください",
        {
          centered: true
        }
      );
      return;
    }
    try {
      this.$loading.show();
      const body = {
        avatar: this.inputValues.avatar,
        hasRegisteredAvatar: true,
        updatedAt: this.requestQueueUpdatedAt
      };
      if (this.hasRegisteredAvatar && this.hasRegisteredProfile) {
        // 初回登録済みのユーザーの場合requestQueueを作成or更新
        await accountService.requestVerificationQueue(body);
      } else {
        // 初回登録がまだのユーザーの場合userDetailを更新
        await authStore.registerUserInfo(body);
      }
      await this.$router.push({ name: "RegistrationInformationAndStatus" });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-avatar {
  min-height: inherit;
}
</style>
